/* eslint-disable css-modules/no-unused-class  */
import { FC, useEffect, useState } from 'react';
import { translate } from 'translations';
import { useSelector } from 'react-redux';
import { selectToken, selectIsLeanUser } from '@redux/slices/userSlice';
import { useAssignedContent } from '@queries/core/homepage';
import { coreService } from 'network-utils';
import { ContentCardProps, Loader } from 'learning-panel-components';
import CardsGalleryContainer from '@components/cardsGallery/cardsGalleryContainer';
import CourseBanner from '@components/learn/courseBanner/courseBanner';
import { CourseBannerProps } from '@components/learn/courseBanner/courseBannerTypes';
import convertLessonToContentCard from '@services/convertLessonToContentCard';
import convertCourseToContentCard from '@services/convertCourseToContentCard';
import convertCourseToBanner from '@services/convertCourseToBanner';
import convertLearningPathToContentCard from '@services/convertLearningPathToContentCard';
import convertSkillTreeToContentCard from '@services/convertSkillTreeToContentCard';
import { progressOrder } from '@components/learningPath/utils';
import styles from './assignedContent.module.scss';

const AssignedContentContainer: FC<{
    setCertificateCourseUuid: Function;
    setIsCustomCourse: Function;
    onContactUsClick: Function;
    fullWidth?: boolean;
}> = ({
    setCertificateCourseUuid,
    setIsCustomCourse,
    onContactUsClick,
    fullWidth
}: {
    setCertificateCourseUuid: Function;
    setIsCustomCourse: Function;
    onContactUsClick: Function;
    fullWidth?: boolean;
}) => {
    const token = useSelector(selectToken);
    const isLeanUser = useSelector(selectIsLeanUser);
    const { data, isLoading } = useAssignedContent(token);
    const [content, setContent] = useState<ContentCardProps[]>([]);
    const [primaryCourse, setPrimaryCourse] = useState<CourseBannerProps | undefined>(undefined);

    useEffect(() => {
        if (!data) return;
        if ((data as any).success === false) return;

        const tempContent: ContentCardProps[] = [];

        if (data.lessons && data.lessons.length > 0) {
            const tempLessons = data.lessons?.map((lesson: coreService.Lesson) =>
                convertLessonToContentCard(lesson, isLeanUser, onContactUsClick)
            );
            tempLessons.sort((a: any, b: any) => {
                const indexA = progressOrder.indexOf(a.status);
                const indexB = progressOrder.indexOf(b.status);
                return indexA - indexB;
            });
            tempContent.push(...tempLessons);
        }

        if (data.courses && data.courses.length > 0) {
            const tempCourses =
                data.courses?.length > 0 &&
                data.courses
                    .filter((course: any) => !course.isPrimary)
                    .map((course: any) =>
                        convertCourseToContentCard(course, isLeanUser, setCertificateCourseUuid, setIsCustomCourse)
                    );

            tempCourses.sort((a: any, b: any) => {
                const indexA = progressOrder.indexOf(a.status);
                const indexB = progressOrder.indexOf(b.status);
                return indexA - indexB;
            });
            tempContent.push(...tempCourses);
            const tempPrimaryCourse = data.courses.find((course: any) => course.isPrimary === true);
            tempPrimaryCourse && setPrimaryCourse(convertCourseToBanner(tempPrimaryCourse));
        }

        if (data.learningPaths && data.learningPaths.length > 0) {
            const tempLearningPaths = data.learningPaths?.map((learningPath: coreService.LearningPath) =>
                convertLearningPathToContentCard(learningPath)
            );

            tempContent.push(...tempLearningPaths);
        }
        if (data.skillTrees && data.skillTrees.length > 0) {
            const tempSkillTrees = data.skillTrees?.map((skillTree: coreService.SkillTree) =>
                convertSkillTreeToContentCard(skillTree)
            );

            tempContent.push(...tempSkillTrees);
        }

        tempContent.sort((a, b) => {
            const dueDateA = a.dueDate ? new Date(a.dueDate) : null;
            const dueDateB = b.dueDate ? new Date(b.dueDate) : null;

            if (dueDateA && dueDateB) {
                return dueDateA.getTime() - dueDateB.getTime();
            }
            if (dueDateA === null) {
                return 1;
            }
            if (dueDateB === null) {
                return -1;
            }

            return 0;
        });

        setContent(tempContent);
    }, [data, isLeanUser]);

    return (
        <>
            <div id="assigned-content-page" className={styles['assigned-content-wrapper']}>
                {isLoading ? (
                    <Loader showBackground={false} />
                ) : (
                    <>
                        {primaryCourse && <CourseBanner {...primaryCourse} />}
                        <div className={styles.container}>
                            {content.length > 0 && (
                                <CardsGalleryContainer
                                    rows={2}
                                    columns={4}
                                    noFullWidthColumn={3}
                                    data={content}
                                    title={translate('AssignedContent')}
                                    fullWidth={fullWidth}
                                    infoDescription={translate('CompleteContentYourManagerAssignedToYou')}
                                />
                            )}
                        </div>
                    </>
                )}
            </div>
        </>
    );
};

export default AssignedContentContainer;
